import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Radio, CheckCircle2, Paperclip, Trash2, ExternalLink } from "lucide-react";
import CommandHeader from "../components/product-detail/CommandHeader";
import GateTimeline from "../components/product-detail/Timeline";
import DetailPanel from "../components/product-detail/DetailPanel";
import AuditFeed from "../components/product-detail/AuditFeed";
import { ProductDetailSkeleton } from "../components/Skeleton";
import FileDropzone, { formatBytes, buildFileDownloadUrl } from "../components/FileDropzone";
import { can } from "../lib/permissions";
import { toast } from "sonner";

export default function ProductDetail() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const requestedGateId = searchParams.get("gate"); // ?gate=<instance_id>

    const [product, setProduct] = useState(null);
    const [gates, setGates] = useState([]);
    const [audits, setAudits] = useState([]);
    const [reliability, setReliability] = useState(null);
    const [fieldReports, setFieldReports] = useState([]);
    const [selectedGateId, setSelectedGateId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        try {
            const [p, g, a, rel, fr] = await Promise.all([
                api.get(`/products/${id}`).then((r) => r.data),
                api.get(`/products/${id}/gate-instances`).then((r) => r.data),
                api
                    .get(`/audit-logs?target_id=${id}&limit=50`)
                    .then((r) => r.data)
                    .catch((err) => {
                        console.warn("[ProductDetail] audit-logs fetch failed", err?.response?.status);
                        return [];
                    }),
                api.get(`/products/${id}/reliability`).then((r) => r.data).catch(() => null),
                api.get(`/field-reports?product_id=${id}`).then((r) => r.data).catch(() => []),
            ]);
            setProduct(p);
            setGates(g || []);
            setAudits(a || []);
            setReliability(rel);
            setFieldReports(fr || []);
            setSelectedGateId((current) => {
                if (requestedGateId && (g || []).some((x) => x.id === requestedGateId)) {
                    return requestedGateId;
                }
                if (current && (g || []).some((x) => x.id === current)) return current;
                if (!g || g.length === 0) return null;
                const inReview = g.find((x) => x.status === "In Review");
                if (inReview) return inReview.id;
                const active = g.find((x) => x.status !== "Passed") || g[0];
                return active.id;
            });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [id, requestedGateId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (requestedGateId && selectedGateId === requestedGateId) {
            const next = new URLSearchParams(searchParams);
            next.delete("gate");
            setSearchParams(next, { replace: true });
        }
    }, [requestedGateId, selectedGateId, searchParams, setSearchParams]);

    if (loading) {
        return <ProductDetailSkeleton />;
    }

    if (error || !product) {
        return (
            <div
                className="border border-red-500/40 bg-red-500/5 p-6"
                data-testid="product-detail-error"
            >
                <div className="overline text-red-400">Product Unavailable</div>
                <div className="heading-display text-xl mt-2">Could not load product</div>
                <div className="text-xs text-snow-dim mt-2 font-mono">{error || "Not found"}</div>
                <Link to="/products" className="cerb-btn-secondary mt-4 inline-flex">
                    ← Back to Products
                </Link>
            </div>
        );
    }

    const selectedGate = gates.find((g) => g.id === selectedGateId);

    return (
        <div className="space-y-6" data-testid="product-detail-root">
            <div className="flex items-center gap-2 text-xs font-mono text-snow-muted">
                <Link
                    to="/products"
                    className="hover:text-signal flex items-center gap-1"
                    data-testid="back-to-products"
                >
                    <ArrowLeft className="w-3 h-3" /> Products
                </Link>
                <span>/</span>
                <span className="text-snow">{product.name}</span>
            </div>

            <CommandHeader product={product} reliability={reliability} onChange={refresh} />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-5">
                    <GateTimeline
                        gates={gates}
                        selectedGateId={selectedGateId}
                        onSelect={setSelectedGateId}
                    />
                </div>
                <div className="lg:col-span-7">
                    <DetailPanel gate={selectedGate} user={user} onChange={refresh} />
                </div>
            </div>

            <FieldReportHistory reports={fieldReports} />

            <ProductAssets product={product} user={user} onChange={refresh} />

            <AuditFeed entries={audits} title="Audit Log — This Product" testId="product-audit-log" />
        </div>
    );
}

function FieldReportHistory({ reports }) {
    return (
        <div className="border border-[#2A2A2A] bg-jet-elev1" data-testid="field-report-history">
            <div className="border-b border-[#2A2A2A] px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Radio className="w-3 h-3 text-signal" />
                    <span className="overline">Field Report History</span>
                </div>
                <span className="text-xs font-mono text-snow-muted tabular-nums">
                    {reports.length} {reports.length === 1 ? "report" : "reports"}
                </span>
            </div>
            {reports.length === 0 ? (
                <div className="px-5 py-8 text-center text-snow-muted text-xs font-mono">
                    No field reports filed against this product.
                </div>
            ) : (
                <ul className="divide-y divide-[#2A2A2A]">
                    {reports.map((r) => {
                        const isCritical = r.severity === "Critical";
                        const open = !r.resolved;
                        const sevCls = isCritical
                            ? "border-[#2A2A2A] text-signal bg-jet-elev1"
                            : r.severity === "High"
                            ? "border-orange-500/40 text-orange-300 bg-orange-500/5"
                            : r.severity === "Medium"
                            ? "border-yellow-500/40 text-yellow-300 bg-yellow-500/5"
                            : "border-[#2A2A2A] text-snow-dim";
                        return (
                            <li
                                key={r.id}
                                data-testid={`product-report-${r.report_id}`}
                                className={`px-5 py-3 ${isCritical && open ? "border-l-2 border-signal bg-signal/5" : ""}`}
                            >
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className={`font-mono text-sm tabular-nums ${isCritical && open ? "text-signal font-bold" : "text-snow"}`}>
                                        {r.report_id}
                                    </span>
                                    <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${sevCls}`}>
                                        {r.severity}
                                    </span>
                                    {r.resolved
                                        ? <span className="inline-flex items-center gap-1 border border-green-500/40 text-green-400 bg-green-500/5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                                            <CheckCircle2 className="w-3 h-3" /> Resolved
                                          </span>
                                        : <span className="inline-flex items-center border border-yellow-500/40 text-yellow-400 bg-yellow-500/5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                                            Open
                                          </span>}
                                    <span className="text-[11px] font-mono text-snow-muted ml-auto">{r.operator_email} · {new Date(r.timestamp).toISOString().slice(0, 16).replace("T", " ")}</span>
                                </div>
                                <div className={`text-sm mt-1 font-mono ${isCritical && open ? "text-signal" : "text-snow-dim"}`}>
                                    {r.issue_description}
                                </div>
                                {r.resolved && r.resolution_notes && (
                                    <div className="text-[11px] text-green-400/90 mt-1 font-mono italic">
                                        Resolved: "{r.resolution_notes}"
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function ProductAssets({ product, user, onChange }) {
    const canManage = can(user, "manageProducts");
    const canDelete = can(user, "deleteProduct");
    const files = product.file_attachments || [];
    const carriedFromIdea = files.filter((f) => f.source === "promote");
    const ongoing = files.filter((f) => f.source !== "promote");

    const detachUrl = `/products/${product.id}/files`;
    const attachEntityUrl = `/products/${product.id}/files`;

    const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);

    const handleDelete = async (file_id) => {
        if (!window.confirm("Remove this asset? This cannot be undone if no other entity references it.")) return;
        try {
            await api.delete(`${detachUrl}/${file_id}`);
            toast.success("Asset removed");
            onChange && onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        }
    };

    return (
        <div className="border border-[#2A2A2A] bg-jet-elev1" data-testid="product-assets">
            <div className="border-b border-[#2A2A2A] px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Paperclip className="w-3 h-3 text-signal" />
                    <span className="overline">Assets &amp; Documentation</span>
                </div>
                <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider tabular-nums">
                    {files.length} files · {formatBytes(totalBytes)} of 50 MB
                </span>
            </div>

            {/* Carried from Concept */}
            <div className="border-b border-[#2A2A2A] px-5 py-4" data-testid="assets-carried-section">
                <div className="overline mb-2">Carried from Concept Intake</div>
                {carriedFromIdea.length === 0 ? (
                    <div className="text-[11px] text-snow-muted font-mono italic">
                        No files were attached to the originating idea (or this product was created directly).
                    </div>
                ) : (
                    <ul className="space-y-1">
                        {carriedFromIdea.map((f) => (
                            <AssetRow
                                key={f.file_id}
                                file={f}
                                badge="From Idea"
                                canDelete={canDelete}
                                onDelete={() => handleDelete(f.file_id)}
                                testId={`asset-carried-${f.file_id}`}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {/* Ongoing Documentation */}
            <div className="px-5 py-4 space-y-3" data-testid="assets-ongoing-section">
                <div className="overline">Ongoing Documentation</div>
                {ongoing.length > 0 && (
                    <ul className="space-y-1">
                        {ongoing.map((f) => (
                            <AssetRow
                                key={f.file_id}
                                file={f}
                                badge="Ongoing"
                                canDelete={canDelete}
                                onDelete={() => handleDelete(f.file_id)}
                                testId={`asset-ongoing-${f.file_id}`}
                            />
                        ))}
                    </ul>
                )}
                {canManage ? (
                    <FileDropzone
                        mode="attach"
                        entityUrl={attachEntityUrl}
                        value={files}
                        onChange={() => onChange && onChange()}
                        testIdPrefix="product-asset-dropzone"
                        canDelete={false}
                        label="Drop new manuals, test results, or runbooks"
                        helpText="Uploaded files appear in 'Ongoing Documentation' above"
                    />
                ) : (
                    <div className="text-[11px] text-snow-muted font-mono italic">
                        Upload requires admin / executive / operator role.
                    </div>
                )}
            </div>
        </div>
    );
}

function AssetRow({ file, badge, canDelete, onDelete, testId }) {
    const isImage = (file.content_type || "").startsWith("image/");
    return (
        <li className="flex items-center gap-3 text-xs font-mono py-1.5 border-b border-[#2A2A2A] last:border-b-0" data-testid={testId}>
            <Paperclip className="w-3 h-3 text-snow-muted flex-shrink-0" />
            <a
                href={buildFileDownloadUrl(file.file_id)}
                target="_blank"
                rel="noreferrer"
                className="flex-1 min-w-0 truncate text-snow hover:text-signal underline-offset-2 hover:underline"
                data-testid={`${testId}-link`}
            >
                {file.filename}
            </a>
            <span className="text-[10px] text-snow-muted tabular-nums whitespace-nowrap">{formatBytes(file.size)}</span>
            <span className={`inline-flex items-center border px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] ${
                badge === "From Idea" ? "border-signal/40 text-signal/90 bg-signal/5" : "border-[#2A2A2A] text-snow-dim"
            }`}>
                {badge}
            </span>
            {!isImage && (
                <a
                    href={buildFileDownloadUrl(file.file_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-snow-muted hover:text-signal p-1"
                    title="Open"
                >
                    <ExternalLink className="w-3 h-3" />
                </a>
            )}
            {canDelete && (
                <button
                    type="button"
                    onClick={onDelete}
                    className="text-snow-muted hover:text-red-400 p-1"
                    data-testid={`${testId}-delete`}
                    title="Remove"
                >
                    <Trash2 className="w-3 h-3" />
                </button>
            )}
        </li>
    );
}

